/**
 * Typed GraphQL operations for the server-side audits (WP4 surface).
 *
 * The API persists each audit as a set of promoted columns plus a single JSON
 * `document` that holds the full client `Audit` shape. The single-audit query
 * and every mutation return the document; the list query omits it (audits can
 * carry multi-MB base64 figures) and instead carries the denormalized counters
 * the sidebar cards render. Mutations return the whole updated audit so the
 * store can fold server truth back in.
 *
 * Mirrors `modules/auth/api.ts`: plain `gql` template literals with typed
 * returns, one thin function per operation.
 */
import { gql } from '@/core/api/client';
import type { Audit, AuditGoal, AuditTier, VideoKind, Verdict } from '@/modules/analyzer/types';

/**
 * A server audit row. `document` is the client `Audit` shape (the server keeps
 * `document.id === id`); it is `null` on rows returned by the list query. The
 * scalar union fields are typed against the client's own unions so the client
 * types stay authoritative.
 */
export interface ServerAudit {
  id: string;
  name: string;
  handle: string;
  tier: AuditTier;
  goal: AuditGoal;
  verdict: Verdict;
  date: string;
  channelUrl: string | null;
  figureCount: number;
  videoCount: number;
  criticalCount: number;
  createdAt: string;
  updatedAt: string;
  document: Audit | null;
}

const AUDIT_FIELDS = /* GraphQL */ `
  fragment AuditFields on Audit {
    id
    name
    handle
    tier
    goal
    verdict
    date
    channelUrl
    figureCount
    videoCount
    criticalCount
    createdAt
    updatedAt
    document
  }
`;

const AUDIT_LIST_FIELDS = /* GraphQL */ `
  fragment AuditListFields on Audit {
    id
    name
    handle
    tier
    goal
    verdict
    date
    channelUrl
    figureCount
    videoCount
    criticalCount
    createdAt
    updatedAt
  }
`;

/** Owner-scoped list, newest-updated first, without the heavy `document`. */
export async function listAudits(): Promise<ServerAudit[]> {
  const data = await gql<{ audits: { items: ServerAudit[] } }>(
    `${AUDIT_LIST_FIELDS}
     query Audits {
       audits(orderBy: updatedAt_DESC, limit: 200) { items { ...AuditListFields } }
     }`,
  );
  return data.audits.items;
}

/** Full audit incl. `document` (or a NotFound if not owned). */
export async function getAudit(id: string): Promise<ServerAudit> {
  const data = await gql<{ audit: ServerAudit }>(
    `${AUDIT_FIELDS}
     query Audit($id: String!) { audit(id: $id) { ...AuditFields } }`,
    { id },
  );
  return data.audit;
}

export async function createAudit(document: Audit): Promise<ServerAudit> {
  const data = await gql<{ createAudit: ServerAudit }>(
    `${AUDIT_FIELDS}
     mutation CreateAudit($input: CreateAuditInput!) {
       createAudit(input: $input) { ...AuditFields }
     }`,
    { input: { document } },
  );
  return data.createAudit;
}

export async function updateAudit(id: string, document: Audit): Promise<ServerAudit> {
  const data = await gql<{ updateAudit: ServerAudit }>(
    `${AUDIT_FIELDS}
     mutation UpdateAudit($input: UpdateAuditInput!) {
       updateAudit(input: $input) { ...AuditFields }
     }`,
    { input: { id, document } },
  );
  return data.updateAudit;
}

export async function deleteAudit(id: string): Promise<string> {
  const data = await gql<{ deleteAudit: { id: string } }>(
    `mutation DeleteAudit($input: DeleteAuditInput!) {
       deleteAudit(input: $input) { id }
     }`,
    { input: { id } },
  );
  return data.deleteAudit.id;
}

export async function analyzeChannel(auditId: string, url: string): Promise<ServerAudit> {
  const data = await gql<{ analyzeChannel: ServerAudit }>(
    `${AUDIT_FIELDS}
     mutation AnalyzeChannel($input: AnalyzeChannelInput!) {
       analyzeChannel(input: $input) { ...AuditFields }
     }`,
    { input: { auditId, url } },
  );
  return data.analyzeChannel;
}

export async function importVideo(auditId: string, url: string, kind: VideoKind): Promise<ServerAudit> {
  const data = await gql<{ importVideo: ServerAudit }>(
    `${AUDIT_FIELDS}
     mutation ImportVideo($input: ImportVideoInput!) {
       importVideo(input: $input) { ...AuditFields }
     }`,
    { input: { auditId, url, kind } },
  );
  return data.importVideo;
}

export async function fetchVideoDetails(auditId: string, videoId: string): Promise<ServerAudit> {
  const data = await gql<{ fetchVideoDetails: ServerAudit }>(
    `${AUDIT_FIELDS}
     mutation FetchVideoDetails($input: FetchVideoDetailsInput!) {
       fetchVideoDetails(input: $input) { ...AuditFields }
     }`,
    { input: { auditId, videoId } },
  );
  return data.fetchVideoDetails;
}

export async function draftCriterion(
  auditId: string,
  criterionId: string,
  steer?: string,
): Promise<ServerAudit> {
  const data = await gql<{ draftCriterion: ServerAudit }>(
    `${AUDIT_FIELDS}
     mutation DraftCriterion($input: DraftCriterionInput!) {
       draftCriterion(input: $input) { ...AuditFields }
     }`,
    { input: { auditId, criterionId, ...(steer ? { steer } : {}) } },
  );
  return data.draftCriterion;
}

export async function draftSummary(auditId: string): Promise<ServerAudit> {
  const data = await gql<{ draftSummary: ServerAudit }>(
    `${AUDIT_FIELDS}
     mutation DraftSummary($input: AuditActionInput!) {
       draftSummary(input: $input) { ...AuditFields }
     }`,
    { input: { auditId } },
  );
  return data.draftSummary;
}

export async function assessHealth(auditId: string): Promise<ServerAudit> {
  const data = await gql<{ assessHealth: ServerAudit }>(
    `${AUDIT_FIELDS}
     mutation AssessHealth($input: AuditActionInput!) {
       assessHealth(input: $input) { ...AuditFields }
     }`,
    { input: { auditId } },
  );
  return data.assessHealth;
}

export async function assessBrandSafety(auditId: string): Promise<ServerAudit> {
  const data = await gql<{ assessBrandSafety: ServerAudit }>(
    `${AUDIT_FIELDS}
     mutation AssessBrandSafety($input: AuditActionInput!) {
       assessBrandSafety(input: $input) { ...AuditFields }
     }`,
    { input: { auditId } },
  );
  return data.assessBrandSafety;
}

export async function assessCreativeReadiness(auditId: string): Promise<ServerAudit> {
  const data = await gql<{ assessCreativeReadiness: ServerAudit }>(
    `${AUDIT_FIELDS}
     mutation AssessCreativeReadiness($input: AuditActionInput!) {
       assessCreativeReadiness(input: $input) { ...AuditFields }
     }`,
    { input: { auditId } },
  );
  return data.assessCreativeReadiness;
}
